/**
 * 图表编辑器「切换图表」轻量常量与映射。
 *
 * ## 本文件放什么（chartSwitch/constants.ts）
 *
 * 基于 `TChartType` 的**轻量**列表/映射：切图条顺序、icon、分组 key、分组标题等。
 * 体量小、结构简单，与 `ChartTypeInfos` 大表分离。
 *
 * ## 与 config.ts 的分工
 *
 * - constants.ts：几十行以内的稳定映射（本文件）
 * - config.ts：数百行的 `ChartTypeInfos` 能力矩阵 + 与之编排的 `SwitchChartTypes`
 */

import { ChartType, type TChartType } from '@dbl/core/chart';

/**
 * 图表切换面板分组元数据中的默认分组 key（未命中具体分组时使用）。
 *
 * @core SwitchChartType、AnalysisLineChart 切图条分组 fallback；放页面会重复。
 */
export const DEFAULT_CHART_GROUP_KEY = 'others';

/**
 * 切换图表面板：按 group key 展示分组标题（中/英）。
 *
 * @core 多维切图 UI 与 Dashboard ChartSwitchTragCard 共用分组文案。
 */
export const ChartGroupMeta = {
  table: {
    nameCn: '表格',
    nameEn: 'Table',
  },
  pie: {
    nameCn: '饼状图',
    nameEn: 'Pie Chart',
  },
  bar: {
    nameCn: '柱状图',
    nameEn: 'Bar Chart',
  },
  line: {
    nameCn: '折线图',
    nameEn: 'Line Chart',
  },
  indicator: {
    nameCn: '指标卡',
    nameEn: 'Indicator Card',
  },
  [DEFAULT_CHART_GROUP_KEY]: {
    nameCn: '其他',
    nameEn: 'Others',
  },
} as const;

/**
 * 图表切换配置项：允许用户在 Table / Line / Bar 之间切换的类型集合。
 *
 * @core ChartSettingsV2 切图与 Dashboard ChartSwitchTragCard 须同一白名单。
 */
export const CHART_SWITCH_OPTIONS = [
  ChartType.Table,
  ChartType.Line,
  ChartType.Bar,
] as const satisfies readonly TChartType[];

/**
 * 图表切换条 UI 展示顺序：柱状图 → 折线图 → 表格（与右上角 icon 一致）。
 *
 * @core Dashboard ChartSwitchTragCard 与多维 ChartTypeSwitchBar 共用顺序。
 */
export const CHART_SWITCH_DISPLAY_ORDER = [
  ChartType.Bar,
  ChartType.Line,
  ChartType.Table,
] as const satisfies readonly TChartType[];

/**
 * 图表切换条 / 设置面板：各 chart_type 对应的多语言 label id。
 *
 * @core 切图条 i18n 与 ChartSettings 面板展示共用 label 映射。
 */
export const CHART_TYPE_LABEL_IDS = {
  [ChartType.Table]: 'dbl_chart_table',
  [ChartType.Line]: 'dbl_chart_line',
  [ChartType.Bar]: 'dbl_chart_bar',
} as const satisfies Partial<Record<TChartType, string>>;

/**
 * 图表切换条：各 chart_type 对应的 tea SvgIcon type 名称。
 *
 * @core Dashboard ChartSwitchTragCard 与多维切图条共用 icon 映射。
 */
export const CHART_TYPE_ICON = {
  [ChartType.Table]: 'sheet',
  [ChartType.Line]: 'discount-chart',
  [ChartType.Bar]: 'column-chart',
} as const satisfies Partial<Record<TChartType, string>>;
